import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import Button from "../../../ui/buttons";
import { COLORS } from "../../../../config/colors";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";

const font = { fontFamily: DEFAULT_FONT_FAMILY };

type Framework = "nodejs" | "nextjs" | "django" | "laravel" | "nginx" | "apache";

interface TabConfig {
  id: Framework;
  label: string;
  filename: string;
  code: string;
  code2?: string;
}

const TRUSTED_DOMAIN = "https://app.cluva.io";

const TABS: TabConfig[] = [
  {
    id: "nodejs",
    label: "Node.js/Express",
    filename: "server.js (or app.js/ index.js)",
    code: `app.use((req, res, next) => {\n  res.setHeader("Content-Security-Policy",\n    "frame-ancestors 'self' ${TRUSTED_DOMAIN}");\n  next();\n});`,
  },
  {
    id: "nextjs",
    label: "Next.js",
    filename: "next.config.js",
    code: `headers: async () => [{\n  source: "/(.*)",\n  headers: [{\n    key: "Content-Security-Policy",\n    value: "frame-ancestors 'self' ${TRUSTED_DOMAIN}"\n  }]\n}]`,
  },
  {
    id: "django",
    label: "Django",
    filename: "settings.py",
    code: `X_FRAME_OPTIONS = 'ALLOW-FROM ${TRUSTED_DOMAIN}'\n# Or with CSP package:\nCSP_FRAME_ANCESTORS = ("'self'", "${TRUSTED_DOMAIN}")`,
    code2: `# add to MIDDLEWARE and create this class\nclass FrameAncestorsMiddleware:\n    def __init__(self, get_response):\n        self.get_response = get_response\n    def __call__(self, request):\n        response = self.get_response(request)\n        response["Content-Security-Policy"] = \\\n            "frame-ancestors 'self' ${TRUSTED_DOMAIN}"\n        if "X-Frame-Options" in response:\n            del response["X-Frame-Options"]\n        return response`,
  },
  {
    id: "laravel",
    label: "Laravel",
    filename: "Middleware file",
    code: `return $next($request)\n    ->header('Content-Security-Policy',\n        "frame-ancestors 'self' ${TRUSTED_DOMAIN}");`,
  },
  {
    id: "nginx",
    label: "Nginx",
    filename: "nginx.conf",
    code: `add_header Content-Security-Policy\n  "frame-ancestors 'self' ${TRUSTED_DOMAIN}";`,
  },
  {
    id: "apache",
    label: "Apache",
    filename: ".htaccess",
    code: `Header always set Content-Security-Policy \\\n  "frame-ancestors 'self' ${TRUSTED_DOMAIN}"`,
  },
];

interface Props {
  onClose: () => void;
  onReload: () => void;
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative mt-2">
      <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs 3xl:text-[14px] text-[#2A3547] font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed">
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded bg-white border border-gray-200 hover:bg-gray-100 text-gray-500 transition-colors"
        title="Copy"
      >
        {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <span className="w-5 h-5 3xl:w-6 3xl:h-6 rounded-full bg-gray-100 text-[#2A354799] text-xs 3xl:text-[14px] flex items-center justify-center shrink-0 mt-0.5 font-medium">
      {n}
    </span>
  );
}

export default function GrantAccessDrawer({ onClose, onReload }: Props) {
  const [activeTab, setActiveTab] = useState<Framework>("nodejs");
  const [isChecked, setIsChecked] = useState(false);

  const tab = TABS.find((t) => t.id === activeTab)!;

  return (
    <div className="fixed top-0 right-0 h-full w-[440px] bg-white shadow-2xl z-[56] flex flex-col border-l border-gray-200" style={font}>
      {/* Header */}
      <div className="px-6 pt-5 pb-3 border-b border-gray-100">
        <h2 className="text-base 3xl:text-[18px] font-semibold text-[#2A3547]">Grant access to OpenUI</h2>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 px-2 overflow-x-auto shrink-0">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setActiveTab(t.id); setIsChecked(false); }}
            className="shrink-0 px-3 py-2.5 text-xs 3xl:text-[14px] font-medium border-b-2 transition-colors whitespace-nowrap"
            style={
              activeTab === t.id
                ? { borderColor: COLORS.buttonActive, color: COLORS.buttonActive, fontFamily: DEFAULT_FONT_FAMILY }
                : { borderColor: "transparent", color: "#2A354799", fontFamily: DEFAULT_FONT_FAMILY }
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Steps */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 custom-scrollbar-overlay">
        {/* Step 1 */}
        <div className="flex items-start gap-3">
          <StepNumber n={1} />
          <div>
            <p className="text-sm 3xl:text-[16px] font-semibold text-[#2A3547]">Open this file</p>
            <span
              className="mt-1.5 inline-block px-2 py-1 text-xs rounded font-mono"
              style={{ backgroundColor: `${COLORS.buttonActive}18`, color: COLORS.buttonActive }}
            >
              {tab.filename}
            </span>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex items-start gap-3">
          <StepNumber n={2} />
          <div className="flex-1 min-w-0">
            <p className="text-sm 3xl:text-[16px] font-semibold text-[#2A3547]">Add the header to your config</p>
            <CodeBlock code={tab.code} />
            {tab.code2 && (
              <>
                <div className="flex items-center gap-2 my-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs 3xl:text-[14px] font-medium text-[#2A354799]">OR</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <CodeBlock code={tab.code2} />
              </>
            )}
          </div>
        </div>

        {/* Step 3 */}
        <div className="flex items-start gap-3">
          <StepNumber n={3} />
          <p className="text-sm 3xl:text-[16px] font-semibold text-[#2A3547]">Save the file.</p>
        </div>

        {/* Step 4 */}
        <div className="flex items-start gap-3">
          <StepNumber n={4} />
          <div>
            <p className="text-sm 3xl:text-[16px] font-semibold text-[#2A3547]">Restart the server</p>
            <p className="text-xs 3xl:text-[14px] font-normal text-[#2A354799] mt-0.5">
              A browser refresh isn't enough, the server must restart.
            </p>
          </div>
        </div>

        {/* Still stuck */}
        <div className="text-center pt-1">
          <p className="text-sm 3xl:text-[16px] font-normal text-[#2A354799]">
            Still stuck?{" "}
            <a
              href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2B6dLViqY1NW0xsxus9tN6Ye-iyqUHkZ-cpXS3IOrjKKVNY1phz1fcQeKMxELax7T4w0jyN440"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline cursor-pointer"
              style={{ color: COLORS.buttonActive }}
            >
              Schedule a meet
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 space-y-3">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 cursor-pointer"
            style={{ accentColor: COLORS.buttonActive }}
          />
          <span className="text-xs 3xl:text-[14px] font-normal text-[#2A354799] leading-relaxed">
            I've completed the setup and restarted my server. I'm ready to reload
            the preview.
          </span>
        </label>
        <div className="flex gap-3">
          <Button
            variant="cancel"
            size="sm"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => { if (isChecked) onReload(); }}
            disabled={!isChecked}
            className="flex-1"
          >
            Reload
          </Button>
        </div>
      </div>
    </div>
  );
}
